import { useEffect, useRef, useState } from 'react';
import type { Subscription } from 'rxjs';
import {getFlightNotifications, downloadFlightFile, downloadDsmTif, sendFlightNotification} from '../api/flights.api';
import type { FlightNotification } from '../api/flights.api';
import { flightCategoriesService } from '@/features/notification/services/flightCategories.service';
import { flightsVisibilityService } from '../services/flightsVisibility.service';
import { useObservable } from '@/shared/hooks/useObservable';
import { mapService } from '@/features/map/services/map.service';

type SendStatus = 'idle' | 'loading' | 'success' | 'error';

function statusLabel(status: FlightNotification['status']) {
  return status === 'submitted'
    ? <span className="text-xs px-1.5 py-0.5 rounded bg-green-900/50 text-green-400 border border-green-800">Oddana / Submitted</span>
    : <span className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">Osnutek / Draft</span>;
}

function categoryLabel(categoryId: number) {
  return flightCategoriesService.getById(String(categoryId))?.name ?? `#${categoryId}`;
}


interface FlightsPanelProps {
  onClose: () => void;
  onDuplicate: (flight: FlightNotification) => void;
}

export function FlightsPanel({ onClose, onDuplicate }: FlightsPanelProps) {
  const [flights, setFlights] = useState<FlightNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sendStatus, setSendStatus] = useState<Record<string, SendStatus>>({});
  const sendSubRef = useRef<Record<string, Subscription>>({});
  const hiddenIds = useObservable(flightsVisibilityService.hiddenIds$, flightsVisibilityService.hiddenIds$.value);

  function handleZoomTo(flight: FlightNotification) {
    if (!flight.takeoff_point) return;
    const [lng, lat] = flight.takeoff_point.coordinates;
    flightsVisibilityService.setVisible(flight.id, true);
    mapService.flyTo(lat, lng, 16);
    onClose();
  }

  function handleSend(id: string) {
    setSendStatus(s => ({ ...s, [id]: 'loading' }));
    sendSubRef.current[id]?.unsubscribe();
    sendSubRef.current[id] = sendFlightNotification(id).subscribe({
      next: () => {
        setSendStatus(s => ({ ...s, [id]: 'success' }));
        setTimeout(() => setSendStatus(s => ({ ...s, [id]: 'idle' })), 3000);
      },
      error: () => setSendStatus(s => ({ ...s, [id]: 'error' })),
    });
  }

  useEffect(() => {
    flightCategoriesService.load();
  }, []);

  useEffect(() => {
    const sub = getFlightNotifications().subscribe({
      next: data => { setFlights(data.slice().reverse()); setLoading(false); },
      error: () => { setError(true); setLoading(false); },
    });
    return () => sub.unsubscribe();
  }, []);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[1000]"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 bottom-0 w-[480px] max-w-full bg-slate-900 border-l border-slate-800 z-[1001] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="h-14 flex items-center gap-3 px-4 border-b border-slate-800 flex-shrink-0">
          <span className="text-sm font-bold text-slate-100 flex-1">Moje najave / My flights</span>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-200 text-lg leading-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Map visibility controls */}
        {!loading && !error && flights.length > 0 && (
          <div className="flex gap-2 px-4 py-2 border-b border-slate-800 flex-shrink-0">
            <button
              onClick={() => flightsVisibilityService.showAll()}
              className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              Prikaži vse / Show all
            </button>
            <span className="text-slate-700">·</span>
            <button
              onClick={() => flightsVisibilityService.hideAll(flights.map(f => f.id))}
              className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              Skrij vse / Hide all
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && (
            <p className="text-sm text-slate-400 text-center py-8">Nalaganje... / Loading...</p>
          )}
          {error && (
            <p className="text-sm text-rose-400 text-center py-8">Napaka pri nalaganju / Error loading flights</p>
          )}
          {!loading && !error && flights.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">Ni najav / No flights yet</p>
          )}
          {flights.map(flight => (
            <div
              key={flight.id}
              className="bg-slate-800 border border-slate-700 rounded-lg p-3 space-y-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={!hiddenIds.has(flight.id)}
                    onChange={e => flightsVisibilityService.setVisible(flight.id, e.target.checked)}
                    title="Prikaži na karti / Show on map"
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-100">{flight.location_name || '—'}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {flight.flight_date} &nbsp;·&nbsp; {flight.time_from}–{flight.time_to}
                    </div>
                  </div>
                </div>
                {statusLabel(flight.status)}
              </div>

              <div className="text-xs text-slate-500">
                {categoryLabel(flight.category)}&nbsp;&nbsp;·&nbsp;&nbsp;
                {flight.purpose || '—'}&nbsp;&nbsp;·&nbsp;&nbsp;
                #{flight.id}
              </div>

              <div className="flex gap-2 pt-1 flex-wrap">
                {flight.takeoff_point && (
                  <button
                    onClick={() => handleZoomTo(flight)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-700 hover:bg-amber-600 text-white transition-colors"
                  >
                    🔍 Prikaži na karti / Show on map
                  </button>
                )}
                <button
                  onClick={() => onDuplicate(flight)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-700 hover:bg-indigo-600 text-white transition-colors"
                >
                  ⎘ Podvoji / Duplicate
                </button>
                {flight.status === 'submitted' && (
                  <>
                    <button
                      onClick={() => downloadFlightFile(flight.id, 'pdf',true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-700 hover:bg-blue-600 text-white transition-colors"
                    >
                      ↓ PDF
                    </button>
                    <button
                      onClick={() => downloadFlightFile(flight.id, 'kml',true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors"
                    >
                      ↓ KML
                    </button>
                    {flight.termination_zone && (
                      <button
                        onClick={() => downloadDsmTif(flight.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-teal-800 hover:bg-teal-700 text-teal-100 transition-colors"
                      >
                        ↓ DSM TIF
                      </button>
                    )}
                    <button
                      onClick={() => handleSend(flight.id)}
                      disabled={sendStatus[flight.id] === 'loading'}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                        sendStatus[flight.id] === 'success'
                          ? 'bg-green-700 hover:bg-green-600 text-white'
                          : sendStatus[flight.id] === 'error'
                          ? 'bg-rose-700 hover:bg-rose-600 text-white'
                          : 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                      }`}
                    >
                      {sendStatus[flight.id] === 'loading' ? 'Sending...'
                        : sendStatus[flight.id] === 'success' ? 'Sent ✓'
                        : sendStatus[flight.id] === 'error' ? 'Error'
                        : 'Send'}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
